// file: src/components/questions/QuestionsPage.js
import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { Card, Table, Button, Form, InputGroup, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getAllQuestions,
  deleteReindexQuestion,
} from "../../services/questionService";
import { useAuth } from "../../contexts/AuthContext";
import QuestionForm from "./QuestionForm";
import QuestionTree from "./QuestionTree";
import Draggable from "react-draggable";
import { ModalDialog } from "react-bootstrap";

const QuestionsPage = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // pakai react-query
  const {
    data: questions,
    isLoading,
    error,
    refetch,
  } = useQuery(["questions"], () => getAllQuestions());

  useEffect(() => {
  const handleResize = () => {
    if (modalRef.current && showForm) {
      const modalWidth = modalRef.current.offsetWidth || 600;
      const modalHeight = modalRef.current.offsetHeight || 400;
      const x = Math.max((window.innerWidth - modalWidth) / 2, 0);
      const y = Math.max((window.innerHeight - modalHeight) / 2, 0);
      setModalPosition({ x, y });
    }
  };

  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, [showForm]);

  useEffect(() => {
  if (showForm && modalRef.current) {
    const modalWidth = modalRef.current.offsetWidth || 600; // default 600px
    const modalHeight = modalRef.current.offsetHeight || 400; // default 400px
    const x = Math.max((window.innerWidth - modalWidth) / 2, 0);
    const y = Math.max((window.innerHeight - modalHeight) / 2, 0);
    setModalPosition({ x, y });
  }
}, [showForm]);

  const handleEdit = (question) => {
    setSelectedQuestion(question);
    setShowForm(true);
  };

  const handleDeleteReindex = (q) => {
    setDeleteData(q);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (q) => {
    setIsDeleting(true);
    try {
      await deleteReindexQuestion(q.path_index);
      toast.success("Pertanyaan berhasil dihapus");
      refetch();
    } catch (err) {
      toast.error("Gagal menghapus pertanyaan");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedQuestion(null);
  };

  const handleFormSuccess = () => {
    refetch();
    handleFormClose();
  };

  if (isLoading) return <div className="loading-spinner">Loading...</div>;
  if (error) return <div className="error-message">Error: {error.message}</div>;

  // filter pencarian sederhana di sisi frontend
  const filteredQuestions = questions?.filter((q) =>
    q.description.toLowerCase().includes(searchTerm.toLowerCase())
  );


  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Data Pertanyaan</h2>
        {(user?.role === "admin" || user?.role === "pengguna") && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            <i className="fas fa-plus me-2"></i>
            Tambah Pertanyaan
          </Button>
        )}
      </div>

      {/* 🔹 Tree View */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Tree Pertanyaan</h5>
          <QuestionTree
            questions={filteredQuestions || []}
            onRefresh={refetch}
            onEdit={handleEdit}
            onDelete={handleDeleteReindex}
          />
        </Card.Body>
      </Card>

      {/* 🔹 Tabel pertanyaan */}
      <Card>
        <Card.Body>
          <div className="row mb-3">
            <div className="col-md-6">
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Cari pertanyaan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>

          <Table responsive striped hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Parent ID</th>
                <th>Deskripsi</th>
                <th>Weight</th>
                <th>Has Child</th>
                <th>Child Format</th>
                <th>Path Index</th>
                {/* <th>Dibuat Oleh</th> */}
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuestions?.map((q, index) => (
                <tr key={q.id}>
                  <td>{index + 1}</td>
                  <td>{q.parent_path_index || "-"}</td>
                  <td>
                    {q.path_index} - {q.description}
                  </td>
                  <td>{q.weight}</td>
                  <td>{q.has_child ? "Ya" : "Tidak"}</td>
                  <td>{q.child_format}</td>
                  <td>{q.path_index}</td>
                  {/* <td>{q.created_by}</td> */}
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => handleEdit(q)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      {(user?.role === "admin" ||
                        user?.role === "pengguna") && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteReindex(q)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
<Modal
  show={showForm}
  onHide={handleFormClose}
  size="lg"
  dialogAs={({ ...props }) => (
    <Draggable
      handle=".modal-header"
      cancel=".btn"
      position={modalPosition}
      onStop={(e, data) => setModalPosition({ x: data.x, y: data.y })}
    >
      <ModalDialog {...props} ref={modalRef} />
    </Draggable>
  )}
>
  <Modal.Header closeButton className="cursor-move d-flex align-items-center justify-content-between">
    <Modal.Title>
      {selectedQuestion ? "Edit Pertanyaan" : "Tambah Pertanyaan"}
    </Modal.Title>
    <Button
      variant="outline-secondary"
      size="sm"
      className="ms-2"
      title="Reset posisi"
      onClick={() => {
        if (modalRef.current) {
          const modalWidth = modalRef.current.offsetWidth || 600;
          const modalHeight = modalRef.current.offsetHeight || 400;
          const x = Math.max((window.innerWidth - modalWidth) / 2, 0);
          const y = Math.max((window.innerHeight - modalHeight) / 2, 0);
          setModalPosition({ x, y });
        } else {
          setModalPosition({ x: 0, y: 0 });
        }
      }}
    >
      <i className="fas fa-sync-alt"></i>
    </Button>
  </Modal.Header>
  <Modal.Body>
    <QuestionForm
      question={selectedQuestion}
      questions={questions || []}
      onSuccess={handleFormSuccess}
      onCancel={handleFormClose}
    />
  </Modal.Body>
</Modal>

      {/* Modal Delete */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Hapus</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Apakah Anda yakin ingin menghapus data{" "}
          {deleteData?.path_index} - {deleteData?.description}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Batal
          </Button>
          <Button
            variant="danger"
            disabled={isDeleting}
            onClick={() => confirmDelete(deleteData)}
          >
            {isDeleting ? "Menghapus..." : "Hapus"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuestionsPage;
